import { useEffect, useRef, useState } from 'react';

const MOBILE_MQ = '(max-width: 768px)';
const SCROLL_DELTA = 8;
const MIN_HIDE_Y = 48;

/**
 * On mobile, hide the app header while scrolling down and reveal it when scrolling up.
 * Desktop and tablet-wide layouts leave the header unchanged.
 */
export function useMobileHeaderAutoHide({ enabled = true, resetKey = null } = {}) {
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    setHidden(false);
    lastScrollY.current = typeof window !== 'undefined' ? window.scrollY : 0;
  }, [resetKey]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;

    const mq = window.matchMedia(MOBILE_MQ);

    const update = () => {
      ticking.current = false;

      if (!mq.matches) {
        setHidden(false);
        return;
      }

      const y = window.scrollY;
      const prev = lastScrollY.current;

      if (y <= 0) {
        setHidden(false);
      } else if (y > prev + SCROLL_DELTA && y > MIN_HIDE_Y) {
        setHidden(true);
      } else if (y < prev - SCROLL_DELTA) {
        setHidden(false);
      }

      lastScrollY.current = y;
    };

    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(update);
    };

    const onBreakpointChange = () => {
      if (!mq.matches) setHidden(false);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    mq.addEventListener('change', onBreakpointChange);

    return () => {
      window.removeEventListener('scroll', onScroll);
      mq.removeEventListener('change', onBreakpointChange);
    };
  }, [enabled]);

  return hidden;
}
